"use client"

import React, { useMemo, useState } from "react"
import { KeyboardShortcut, useKeyboardShortcutContext } from "@/contexts/keyboard-shortcut-context"
import { formatShortcutKeys } from "@/utils/format-shortcut-keys"
import { Button, Input, Popover, PopoverContent, PopoverTrigger } from "@ui/shad"
import { KeyboardShortcut as KeyboardShortcutDisplay } from "@ui/shad/keyboard-shortcut"
import { Keyboard, Search } from "lucide-react"

interface ShortcutGroupProps {
  title: string
  shortcuts: KeyboardShortcut[]
}

function ShortcutGroup({ title, shortcuts }: ShortcutGroupProps) {
  if (shortcuts.length === 0) return null
  const os = typeof navigator !== "undefined" ? navigator.platform.toLowerCase() : ""

  return (
    <div className="-mx-2 border-t px-1.5 pt-2">
      <h3 className="text-muted-foreground ml-1 text-[11px] font-semibold tracking-wider uppercase">{title}</h3>
      <div className="mt-0.5 space-y-0.5">
        {shortcuts.map((shortcut) => (
          <div
            key={shortcut.id}
            className="hover:bg-secondary flex items-center justify-between rounded-lg px-1 py-1 text-sm/none"
          >
            <span className="text-foreground">{shortcut.description}</span>
            <KeyboardShortcutDisplay keys={shortcut.formatted || formatShortcutKeys(shortcut.keys, os)} />
          </div>
        ))}
      </div>
    </div>
  )
}

export function KeyboardShortcutHelper() {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const { shortcuts } = useKeyboardShortcutContext()

  React.useEffect(() => {
    const handleToggle = () => setOpen((prev) => !prev)
    window.addEventListener("toggle-keyboard-shortcuts", handleToggle)
    return () => window.removeEventListener("toggle-keyboard-shortcuts", handleToggle)
  }, [])

  const filteredShortcuts = useMemo(() => {
    const globalShortcuts = shortcuts.filter((s) => s.category === "Global")
    const navigationShortcuts = shortcuts.filter((s) => s.category === "Navigation")
    const actionShortcuts = shortcuts.filter((s) => s.category === "Actions")
    const pageShortcuts = shortcuts.filter((s) => s.category === "Page")

    if (!searchQuery) {
      return {
        global: globalShortcuts,
        navigation: navigationShortcuts,
        actions: actionShortcuts,
        page: pageShortcuts,
      }
    }

    const query = searchQuery.toLowerCase()
    const filterShortcuts = (shortcuts: KeyboardShortcut[]) =>
      shortcuts.filter(
        (shortcut) => shortcut.description.toLowerCase().includes(query) || shortcut.keys.toLowerCase().includes(query)
      )

    return {
      global: filterShortcuts(globalShortcuts),
      navigation: filterShortcuts(navigationShortcuts),
      actions: filterShortcuts(actionShortcuts),
      page: filterShortcuts(pageShortcuts),
    }
  }, [searchQuery, shortcuts])

  const hasShortcuts =
    filteredShortcuts.global.length > 0 ||
    filteredShortcuts.navigation.length > 0 ||
    filteredShortcuts.actions.length > 0 ||
    filteredShortcuts.page.length > 0

  const handleOpenChange = React.useCallback((newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      setSearchQuery("")
    }
  }, [])

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="iconSm">
          <Keyboard />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="h-fit w-80 p-0">
        <div className="flex flex-col">
          <div className="flex items-center gap-2 p-2">
            <Search className="text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search shortcuts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="placeholder:text-muted-foreground h-6 border-0 px-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
          <div className="space-y-2 px-2 pb-2">
            {!hasShortcuts && searchQuery && (
              <p className="text-muted-foreground text-center text-sm">
                No shortcuts found matching &quot;{searchQuery}&quot;
              </p>
            )}
            {!hasShortcuts && !searchQuery && (
              <p className="text-muted-foreground text-center text-sm">No keyboard shortcuts available</p>
            )}
            <ShortcutGroup title="Global" shortcuts={filteredShortcuts.global} />
            <ShortcutGroup title="Current Page" shortcuts={filteredShortcuts.page} />
            <ShortcutGroup title="Actions" shortcuts={filteredShortcuts.actions} />
            <ShortcutGroup title="Navigation" shortcuts={filteredShortcuts.navigation} />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
